<template>
  <div class="contact-advanced-filters flex flex-col">
    <woot-modal-header-v2
      v-if="!skipHeader"
      :header-title="filterModalHeaderTitle"
      :header-content="filterModalSubTitle"
    >
      <template #titleIcon>
        <v2-icon icon="filter_alt" :size="24" class="text-gray-500" />
      </template>
    </woot-modal-header-v2>

    <div class="contact-advanced-filters__body px-6 py-4 flex-1">
      <div v-if="isSegmentsView">
        <label class="input-label" :class="{ error: !activeSegmentNewName }">
          {{ $t('CONTACTS_FILTER.SEGMENT_LABEL') }}
          <input
            v-model="activeSegmentNewName"
            type="text"
            class="folder-input border-slate-75 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
          />
          <span v-if="!activeSegmentNewName" class="message">
            {{ $t('CONTACTS_FILTER.EMPTY_VALUE_ERROR') }}
          </span>
        </label>
        <label class="input-label">
          {{ $t('CONTACTS_FILTER.SEGMENT_QUERY_LABEL') }}
        </label>
      </div>
      <div class="flex flex-col">
        <filter-input-box
          v-for="(filter, i) in appliedFilters"
          :key="i"
          v-model="appliedFilters[i]"
          :filter-groups="filterGroups"
          :grouped-filters="true"
          :input-type="
            getInputType(
              appliedFilters[i].attribute_key,
              appliedFilters[i].filter_operator
            )
          "
          :operators="getOperators(appliedFilters[i].attribute_key)"
          :dropdown-values="getDropdownValues(appliedFilters[i].attribute_key)"
          :show-query-operator="i !== appliedFilters.length - 1"
          :show-user-input="showUserInput(appliedFilters[i].filter_operator)"
          :show-filter-operator="true"
          :show-filter-type="true"
          :show-input-label="true"
          :contacts-layout="true"
          :v="$v.appliedFilters.$each[i]"
          :query-operators="queryOperators"
          :disable-remove-filter="appliedFilters.length === 1"
          @resetFilter="resetFilter(i, appliedFilters[i])"
          @removeFilter="removeFilter(i)"
        />

        <div class="mt-4">
          <button-v2
            variant="outline"
            size="md"
            icon="add"
            class="add-filter-btn"
            @click="appendNewFilter"
          >
            {{ $t('CONTACTS_FILTER.ADD_NEW_FILTER') }}
          </button-v2>
          <button-v2
            v-if="hasAppliedFilters && !isSegmentsView"
            variant="ghost"
            size="md"
            icon="remove"
            class="add-filter-btn ml-3"
            @click="clearFilters"
          >
            {{ $t('CONTACTS_FILTER.CLEAR_ALL_FILTERS') }}
          </button-v2>
        </div>
      </div>
    </div>

    <modal-footer-v2
      v-if="!skipButtons"
      :cancel-text="$t('CONTACTS_FILTER.CANCEL_BUTTON_LABEL')"
      :confirm-text="
        isSegmentsView
          ? $t('CONTACTS_FILTER.UPDATE_BUTTON_LABEL')
          : $t('CONTACTS_FILTER.SUBMIT_BUTTON_LABEL')
      "
      :is-disabled="isSegmentsView ? !activeSegmentNewName : !hasValidFilters"
      @cancel="onClose"
      @confirm="isSegmentsView ? updateSegment() : submitFilterQuery()"
    />
  </div>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import { required, requiredIf } from 'vuelidate/lib/validators';
import FilterInputBox from '../../../../components/widgets/FilterInput/IndexV2.vue';
import V2Icon from 'v2/components/Icon.vue';
import countries from 'shared/constants/countries.js';
import { mapGetters } from 'vuex';
import { filterAttributeGroups } from '../contactFilterItems';
import filterMixin from 'shared/mixins/filterMixin';
import * as OPERATORS from 'dashboard/components/widgets/FilterInput/FilterOperatorTypes.js';
import { CONTACTS_EVENTS } from '../../../../helper/AnalyticsHelper/events';
export default {
  components: {
    FilterInputBox,
    V2Icon,
  },
  mixins: [alertMixin, filterMixin],
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
    initialFilterTypes: {
      type: Array,
      default: () => [],
    },
    initialAppliedFilters: {
      type: Array,
      default: () => [],
    },
    isSegmentsView: {
      type: Boolean,
      default: false,
    },
    activeSegmentName: {
      type: String,
      default: '',
    },
    skipHeader: {
      type: Boolean,
      default: false,
    },
    skipButtons: {
      type: Boolean,
      default: false,
    },
  },
  validations: {
    appliedFilters: {
      required,
      $each: {
        attribute_key: { required },
        values: {
          ensureBetween0to999(value, prop) {
            if (prop.filter_operator === 'days_before') {
              return parseInt(value, 10) > 0 && parseInt(value, 10) < 999;
            }
            return true;
          },
          required: requiredIf(prop => {
            return !(
              prop.filter_operator === 'is_present' ||
              prop.filter_operator === 'is_not_present'
            );
          }),
        },
      },
    },
  },
  data() {
    return {
      show: true,
      appliedFilters: this.initialAppliedFilters,
      activeSegmentNewName: this.activeSegmentName,
      filterTypes: this.initialFilterTypes,
      filterGroups: [],
      allCustomAttributes: [],
      filterAttributeGroups,
      attributeModel: 'contact_attribute',
      filtersFori18n: 'CONTACTS_FILTER',
      queryOperators: [
        { value: 'and', label: 'CONTACTS_FILTER.QUERY_DROPDOWN_LABELS.AND' },
        { value: 'or', label: 'CONTACTS_FILTER.QUERY_DROPDOWN_LABELS.OR' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getAppliedContactFilters: 'contacts/getAppliedContactFilters',
    }),
    hasAppliedFilters() {
      return this.getAppliedContactFilters.length;
    },
    filterModalHeaderTitle() {
      return !this.isSegmentsView
        ? this.$t('CONTACTS_FILTER.TITLE')
        : this.$t('CONTACTS_FILTER.EDIT_CUSTOM_SEGMENT');
    },
    filterModalSubTitle() {
      return !this.isSegmentsView
        ? this.$t('CONTACTS_FILTER.SUBTITLE')
        : this.$t('CONTACTS_FILTER.CUSTOM_VIEWS_SUBTITLE');
    },
    hasValidFilters() {
      return this.appliedFilters.every(filter => {
        if (!filter.attribute_key) return false;
        const op = filter.filter_operator;
        if (op === 'is_present' || op === 'is_not_present') return true;
        if (Array.isArray(filter.values)) return filter.values.length > 0;
        return (
          filter.values !== '' &&
          filter.values !== null &&
          filter.values !== undefined
        );
      });
    },
  },
  watch: {
    appliedFilters: {
      handler(value) {
        this.$emit('onFiltersChange', value);
      },
    },
  },
  mounted() {
    this.setFilterAttributes();
    if (this.getAppliedContactFilters.length) {
      this.appliedFilters = [...this.getAppliedContactFilters];
    } else if (!this.isSegmentsView && !this.appliedFilters.length) {
      this.appliedFilters.push({
        attribute_key: '',
        filter_operator: 'equal_to',
        values: '',
        query_operator: 'and',
        attribute_model: 'standard',
      });
    }
  },
  methods: {
    getOperatorTypes(key) {
      switch (key) {
        case 'list':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'text':
          return OPERATORS.OPERATOR_TYPES_3;
        case 'number':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'link':
          return OPERATORS.OPERATOR_TYPES_1;
        case 'date':
          return OPERATORS.OPERATOR_TYPES_4;
        case 'checkbox':
          return OPERATORS.OPERATOR_TYPES_1;
        default:
          return OPERATORS.OPERATOR_TYPES_1;
      }
    },
    customAttributeInputType(key) {
      switch (key) {
        case 'date':
          return 'date';
        case 'text':
          return 'plain_text';
        case 'list':
          return 'search_select';
        case 'checkbox':
          return 'search_select';
        default:
          return 'plain_text';
      }
    },
    getAttributeModel(key) {
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type.attributeModel;
    },
    getInputType(key, operator) {
      if (!key) return 'search_select';
      if (key === 'created_at' || key === 'last_activity_at')
        if (operator === 'days_before') return 'plain_text';
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.inputType;
    },
    getOperators(key) {
      if (!key) return OPERATORS.OPERATOR_TYPES_1;
      const type = this.filterTypes.find(filter => filter.attributeKey === key);
      return type?.filterOperators;
    },
    getDropdownValues(type) {
      const allCustomAttributes = this.$store.getters[
        'attributes/getAttributesByModel'
      ](this.attributeModel);
      const isCustomAttributeCheckbox = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type &&
          attr.attribute_display_type === 'checkbox'
        );
      });
      const TRUE_FALSE_OPTIONS = [
        {
          id: true,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
        },
        {
          id: false,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
        },
      ];
      if (isCustomAttributeCheckbox) {
        return TRUE_FALSE_OPTIONS;
      }

      const isCustomAttributeList = allCustomAttributes.find(attr => {
        return (
          attr.attribute_key === type && attr.attribute_display_type === 'list'
        );
      });

      if (isCustomAttributeList) {
        return allCustomAttributes
          .find(attr => attr.attribute_key === type)
          .attribute_values.map(item => {
            return {
              id: item,
              name: item,
            };
          });
      }
      switch (type) {
        case 'labels':
          return this.$store.getters['labels/getLabels'].map(i => {
            return {
              id: i.title,
              name: i.title,
            };
          });
        case 'country_code':
          return countries;
        case 'blocked':
          return TRUE_FALSE_OPTIONS;
        default:
          return undefined;
      }
    },
    appendNewFilter() {
      if (this.isSegmentsView) {
        this.setQueryOperatorOnLastQuery();
      } else {
        this.appliedFilters.push({
          attribute_key: '',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      }
    },
    setQueryOperatorOnLastQuery() {
      const lastItemIndex = this.appliedFilters.length - 1;
      this.appliedFilters[lastItemIndex] = {
        ...this.appliedFilters[lastItemIndex],
        query_operator: 'and',
      };
      this.$nextTick(() => {
        this.appliedFilters.push({
          attribute_key: '',
          filter_operator: 'equal_to',
          values: '',
          query_operator: 'and',
        });
      });
    },
    removeFilter(index) {
      if (this.appliedFilters.length <= 1) {
        this.showAlert(this.$t('CONTACTS_FILTER.FILTER_DELETE_ERROR'));
      } else {
        this.appliedFilters.splice(index, 1);
      }
    },
    isFiltersValid() {
      this.$v.$touch();
      return !this.$v.$invalid;
    },
    submitFilterQuery() {
      if (!this.isFiltersValid()) return;
      this.$emit('applyFilter', this.appliedFilters);
      this.$store.dispatch(
        'contacts/setContactFilters',
        JSON.parse(JSON.stringify(this.appliedFilters))
      );
      this.$track(CONTACTS_EVENTS.APPLY_FILTER, {
        applied_filters: this.appliedFilters.map(filter => ({
          key: filter.attribute_key,
          operator: filter.filter_operator,
          query_operator: filter.query_operator,
        })),
      });
    },
    updateSegment() {
      if (!this.isFiltersValid()) return;
      this.$emit(
        'updateSegment',
        this.appliedFilters,
        this.activeSegmentNewName
      );
    },
    resetFilter(index, currentFilter) {
      this.appliedFilters[index].filter_operator = this.filterTypes.find(
        filter => filter.attributeKey === currentFilter.attribute_key
      ).filterOperators[0].value;
      this.appliedFilters[index].values = '';
    },
    showUserInput(operatorType) {
      if (operatorType === 'is_present' || operatorType === 'is_not_present')
        return false;
      return true;
    },
    clearFilters() {
      this.$emit('clearFilters');
      this.onClose();
    },
  },
};
</script>
<style lang="scss">
.contact-advanced-filter-modal {
  .modal-container {
    overflow: visible;
  }

  .contact-advanced-filters {
    max-height: 90vh;
  }

  .contact-advanced-filters__body {
    min-height: 0;
    overflow-y: auto;
  }

  .filter-input-v2__field {
    flex: 0 0 205px;
    max-width: none;
  }

  .filter-input-v2__operator {
    flex: 0 0 130px;
    max-width: none;
  }

  .multiselect__content-wrapper {
    z-index: 100;
  }
}
</style>
<style lang="scss" scoped>
.folder-input {
  @apply w-[50%];
}

.add-filter-btn {
  @apply text-base leading-6 px-4;
}
</style>
